import type {
  ElementInfo,
  ImagePrompt,
  ReferencePrompt,
  TextPromptMentionMediaType,
  TextPromptWithInputItem,
  VideoPrompt,
  ViduV1Element,
} from '@/app/types';
import { getMentionColor } from '.';

const getReferenceMentionMetadata = (reference: ReferencePrompt) => {
  let preview: string | undefined;
  let mediaType: TextPromptMentionMediaType | undefined;

  if (reference.type === 'image') {
    const prompt = reference.prompt as ImagePrompt | null | undefined;
    preview =
      prompt?.selectedImagePreview || prompt?.sourceImagePreview || prompt?.selectedImage || prompt?.sourceImage;
    mediaType = 'image';
  } else if (reference.type === 'video') {
    const prompt = reference.prompt as VideoPrompt | null | undefined;
    preview = prompt?.coverImagePreview || prompt?.coverImage;
    mediaType = 'video';
  } else if (reference.type === 'element') {
    const component = (reference.prompt as ElementInfo | null | undefined)?.components?.[0];
    preview = component?.cover_uri || component?.content;
  }

  return {
    ...(preview ? { preview } : {}),
    ...(mediaType ? { mediaType } : {}),
  };
};

const createReferenceMention = (reference: ReferencePrompt) => ({
  id: reference.id,
  name: reference.name,
  color: getMentionColor({ element: reference.prompt as ViduV1Element, type: reference.type }),
  ...getReferenceMentionMetadata(reference),
});

/**
 * 将 TextPromptWithInputItem 对象转换为纯文本
 *
 * 将 mention 节点变成 [@name]
 *
 * @param prompt - 要转换的 TextPromptWithInputItem 对象
 * @param isIgnoreMention - 是否忽略 mention 节点
 * @returns 转换后的纯文本
 */
export const stringifyTextPromptWithReference = (prompt: TextPromptWithInputItem, isIgnoreMention = false) => {
  return prompt
    .map((item) =>
      item.type === 'mention'
        ? isIgnoreMention
          ? ''
          : `[@${item.mention.name}]`
        : item.type === 'text'
          ? item.text
          : '',
    )
    .join('');
};

/**
 * 将纯文本转换为 TextPromptWithInputItem 对象
 *
 * 将 [@name] 变成 mention 节点
 *
 * @param content - 要转换的纯文本
 * @param referencePrompts - 参考列表
 * @returns 转换后的 TextPromptWithInputItem 对象
 */
export const parseTextToTextPromptWithReference = (
  content: string,
  referencePrompts?: ReferencePrompt[],
): TextPromptWithInputItem => {
  if (!referencePrompts?.length) return content ? [{ type: 'text', text: content }] : [];

  const textPromptWithReference: TextPromptWithInputItem = [];

  // 匹配文本中的 [@name] 格式
  const mentionRegex = /\[@([^\]]+)\]/g;
  let lastIndex = 0;
  let match = mentionRegex.exec(content);
  while (match !== null) {
    const mentionName = match[1];
    // 查找对应的参考
    const reference = referencePrompts.find((ref) => ref.name === mentionName);

    if (reference) {
      // 只有找到对应的 reference 时才处理为 mention，并更新 lastIndex
      // 添加匹配前的文本
      if (match.index > lastIndex) {
        textPromptWithReference.push({
          type: 'text',
          text: content.substring(lastIndex, match.index),
        });
      }

      textPromptWithReference.push({
        type: 'mention',
        mention: createReferenceMention(reference),
      });

      lastIndex = match.index + match[0].length;
    }

    // 如果没找到 reference，不更新 lastIndex，这样 [@xxx] 格式的文本会被包含在下一段文本中
    match = mentionRegex.exec(content);
  }

  // 添加最后一段文本
  if (lastIndex < content.length) {
    textPromptWithReference.push({
      type: 'text',
      text: content.substring(lastIndex),
    });
  }

  // 如果没有任何mention，则作为单个文本段
  if (textPromptWithReference.length === 0 && content) {
    textPromptWithReference.push({
      type: 'text',
      text: content,
    });
  }

  return textPromptWithReference;
};

/**
 * 检查是否有未在文本中提及的参考，将它们添加到文本末尾
 *
 * @param textPromptWithReference - 要检查的 TextPromptWithInputItem 对象
 * @param referencePrompts - 参考列表
 * @returns 添加未提及的参考后的 TextPromptWithInputItem 对象
 */
export const appendUnMentionedReferences = (
  textPromptWithReference: TextPromptWithInputItem,
  referencePrompts: ReferencePrompt[],
): TextPromptWithInputItem => {
  const mentionedRefIds = textPromptWithReference
    .filter((item) => item.type === 'mention')
    .map((item) => (item.type === 'mention' ? item.mention.id : ''));

  const unMentionedReferences = referencePrompts.filter((ref) => !mentionedRefIds.includes(ref.id));

  if (unMentionedReferences.length > 0) {
    // 如果最后一个元素是文本，并且不是以空格结尾，添加一个空格
    const lastItem = textPromptWithReference[textPromptWithReference.length - 1];
    if (lastItem && lastItem.type === 'text' && lastItem.text.length > 0 && !lastItem.text.endsWith(' ')) {
      lastItem.text += ' ';
    }

    // 添加未提及的参考
    unMentionedReferences.forEach((ref) => {
      textPromptWithReference.push({
        type: 'mention',
        mention: createReferenceMention(ref),
      });
    });
  }

  return textPromptWithReference;
};
